import { Link } from 'react-router-dom';
import { Download, Eye, Pencil, Trash2 } from 'lucide-react';
import { useDeleteDocument } from '../hooks/useApi';
import { downloadUrl, errorMessage } from '../services/api';
import { toast } from '../store';
import type { Document, GPSMonth, GPSRide, Service } from '../types';
import {
  daysUntil,
  driverName,
  formatCurrency,
  formatDate,
  formatDuration,
  formatFileSize,
  formatKm,
  formatMonth,
  formatNumber,
  formatTime,
} from '../utils/helpers';
import { DocTypeBadge, ExpiryBadge, ServiceTypeBadge } from './ui/Badge';
import type { Column } from './ui/DataTable';
import { ConfirmDialog } from './ui/Modal';
import { t, tx } from '../i18n';

export function serviceColumns({
  onEdit,
  onDelete,
  showCar,
}: {
  onEdit: (s: Service) => void;
  onDelete: (s: Service) => void;
  showCar: boolean;
}): Column<Service>[] {
  const columns: Column<Service>[] = [
    { key: 'date', header: t('Dátum'), className: 'whitespace-nowrap', render: (s) => formatDate(s.service_date) },
    ...(showCar
      ? [
          {
            key: 'car',
            header: t('Vozidlo'),
            render: (s: Service) => (
              <Link to={`/cars/${s.car_id}?tab=services`} className="whitespace-nowrap font-mono font-medium text-primary-700 hover:underline">
                {s.car?.license_plate}
              </Link>
            ),
          },
        ]
      : []),
    { key: 'type', header: t('Typ'), render: (s) => <ServiceTypeBadge type={s.service_type} /> },
    {
      key: 'description',
      header: t('Popis'),
      render: (s) => (
        <div className="max-w-xs">
          <p className="line-clamp-2">{s.description || '–'}</p>
          {s.workshop && <p className="text-xs text-secondary-500">{s.workshop}</p>}
        </div>
      ),
    },
    { key: 'km', header: t('Km'), className: 'text-right tabular-nums', headerClassName: 'text-right', render: (s) => formatNumber(s.km_at_service) },
    { key: 'cost', header: t('Cena'), className: 'text-right tabular-nums whitespace-nowrap', headerClassName: 'text-right', render: (s) => formatCurrency(s.cost) },
    {
      key: 'next',
      header: t('Ďalší servis'),
      className: 'whitespace-nowrap',
      render: (s) => (
        <div className="text-sm">
          {s.next_service_date && <ExpiryBadge days={daysUntil(s.next_service_date)} date={formatDate(s.next_service_date)} />}
          {s.next_service_km !== null && <p className="text-xs text-secondary-500">{formatNumber(s.next_service_km)} km</p>}
          {!s.next_service_date && s.next_service_km === null && '–'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (s) => (
        <>
          <button className="icon-btn" onClick={() => onEdit(s)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="icon-btn-danger" onClick={() => onDelete(s)} title={t('Zmazať')} aria-label={t('Zmazať')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];
  return columns;
}

export function DeleteDocumentDialog({
  document,
  onClose,
  remove,
}: {
  document: Document | null;
  onClose: () => void;
  remove: ReturnType<typeof useDeleteDocument>;
}) {
  return (
    <ConfirmDialog
      isOpen={!!document}
      onClose={onClose}
      loading={remove.isPending}
      title={t('Zmazať dokument')}
      confirmText={t('Zmazať')}
      message={
        <>
          {tx('Naozaj zmazať dokument {title}? Súbor bude natrvalo odstránený.', { title: <strong>{document?.title}</strong> })}
        </>
      }
      onConfirm={async () => {
        try {
          await remove.mutateAsync(document!.id);
          toast.success(t('Dokument bol zmazaný'));
        } catch (err) {
          toast.error(errorMessage(err));
        }
        onClose();
      }}
    />
  );
}

export function documentColumns({
  onEdit,
  onDelete,
  showCar,
}: {
  onEdit: (d: Document) => void;
  onDelete: (d: Document) => void;
  showCar: boolean;
}): Column<Document>[] {
  const viewable = (d: Document) => d.mime_type === 'application/pdf' || d.mime_type?.startsWith('image/');
  return [
    {
      key: 'title',
      header: t('Názov'),
      render: (d) => (
        <div className="min-w-[10rem]">
          <p className="font-medium">{d.title}</p>
          <p className="text-xs text-secondary-500">
            {d.original_filename} · {formatFileSize(d.file_size)}
          </p>
        </div>
      ),
    },
    ...(showCar
      ? [
          {
            key: 'car',
            header: t('Vozidlo'),
            render: (d: Document) => (
              <Link to={`/cars/${d.car_id}?tab=documents`} className="whitespace-nowrap font-mono font-medium text-primary-700 hover:underline">
                {d.car?.license_plate}
              </Link>
            ),
          },
        ]
      : []),
    { key: 'type', header: t('Typ'), render: (d) => <DocTypeBadge type={d.doc_type} /> },
    { key: 'expiry', header: t('Platnosť do'), className: 'whitespace-nowrap', render: (d) => <ExpiryBadge days={daysUntil(d.expiry_date)} date={formatDate(d.expiry_date)} /> },
    { key: 'uploaded', header: t('Nahrané'), className: 'whitespace-nowrap', render: (d) => formatDate(d.uploaded_at) },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (d) => (
        <>
          {viewable(d) && (
            <a className="icon-btn" href={downloadUrl(d.id, true)} target="_blank" rel="noreferrer" title={t('Zobraziť')} aria-label={t('Zobraziť')}>
              <Eye className="h-4 w-4" />
            </a>
          )}
          <a className="icon-btn" href={downloadUrl(d.id)} title={t('Stiahnuť')} aria-label={t('Stiahnuť')}>
            <Download className="h-4 w-4" />
          </a>
          <button className="icon-btn" onClick={() => onEdit(d)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="icon-btn-danger" onClick={() => onDelete(d)} title={t('Zmazať')} aria-label={t('Zmazať')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];
}

export function rideColumns(showCar: boolean): Column<GPSRide>[] {
  return [
    {
      key: 'date',
      header: t('Dátum'),
      className: 'whitespace-nowrap',
      render: (r) => (
        <div>
          <p>{formatDate(r.ride_date)}</p>
          <p className="text-xs text-secondary-500">
            {formatTime(r.start_time)} – {formatTime(r.end_time)}
          </p>
        </div>
      ),
    },
    ...(showCar
      ? [
          {
            key: 'car',
            header: t('Vozidlo'),
            render: (r: GPSRide) => (
              <Link to={`/cars/${r.car_id}?tab=rides`} className="whitespace-nowrap font-mono font-medium text-primary-700 hover:underline">
                {r.car?.license_plate}
              </Link>
            ),
          },
        ]
      : []),
    { key: 'driver', header: t('Vodič'), render: (r) => (r.driver ? driverName(r.driver) : '–') },
    {
      key: 'route',
      header: t('Trasa'),
      render: (r) => (
        <div className="max-w-sm text-sm">
          <p className="truncate" title={r.start_location ?? undefined}>{r.start_location || '–'}</p>
          <p className="truncate text-secondary-500" title={r.end_location ?? undefined}>→ {r.end_location || '–'}</p>
        </div>
      ),
    },
    { key: 'distance', header: t('Vzdialenosť'), className: 'text-right tabular-nums whitespace-nowrap', headerClassName: 'text-right', render: (r) => formatKm(r.distance_km) },
    { key: 'duration', header: t('Doba'), className: 'whitespace-nowrap', render: (r) => formatDuration(r.duration_minutes) },
    { key: 'purpose', header: t('Účel'), render: (r) => r.purpose || '–' },
  ];
}

/** Monthly GPS records (one car, one month). */
export function gpsMonthColumns({
  showCar,
  onDelete,
}: {
  showCar: boolean;
  onDelete?: (m: GPSMonth) => void;
}): Column<GPSMonth>[] {
  return [
    {
      key: 'month',
      header: t('Mesiac'),
      className: 'whitespace-nowrap',
      render: (m) => (
        <Link to={`/gps?month=${m.id}`} className="font-medium text-primary-700 hover:underline">
          {formatMonth(m.year, m.month)}
        </Link>
      ),
    },
    ...(showCar
      ? [
          {
            key: 'car',
            header: t('Vozidlo'),
            render: (m: GPSMonth) => (
              <Link to={`/cars/${m.car_id}?tab=rides`} className="whitespace-nowrap hover:underline">
                <span className="font-mono font-medium text-primary-700">{m.car?.license_plate}</span>
                <span className="block text-xs text-secondary-500">
                  {m.car?.brand} {m.car?.model}
                </span>
              </Link>
            ),
          },
        ]
      : []),
    {
      key: 'km',
      header: t('Najazdené'),
      className: 'text-right tabular-nums whitespace-nowrap',
      headerClassName: 'text-right',
      render: (m) => (
        <div>
          <p className="font-medium">{formatKm(m.distance_km)}</p>
          <p className="text-xs text-secondary-500">
            {t('{count} jázd', { count: formatNumber(m.ride_count) })}
            {m.private_km ? t(' · súkr. {privateKm}', { privateKm: formatKm(m.private_km) }) : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'odometer',
      header: t('Tachometer'),
      className: 'whitespace-nowrap tabular-nums text-sm',
      render: (m) => (m.odometer_end !== null ? `${formatNumber(m.odometer_start)} → ${formatNumber(m.odometer_end)}` : '–'),
    },
    {
      key: 'places',
      header: t('Navštívené miesta'),
      render: (m) => (
        <p className="line-clamp-2 min-w-[12rem] max-w-md text-sm" title={m.places ?? undefined}>
          {m.places || '–'}
        </p>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (m) =>
        onDelete && (
          <button className="icon-btn-danger" onClick={() => onDelete(m)} title={t('Zmazať mesiac')} aria-label={t('Zmazať mesiac')}>
            <Trash2 className="h-4 w-4" />
          </button>
        ),
    },
  ];
}
